import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { getProducts, getDeliveryCompanies, getConfirmationAgents, createManualOrder, getProductVariants } from '../services/api';
import { getCitiesByProvider, ensureCitiesLoaded } from '../services/citiesService';
import CityCombobox from '../components/CityCombobox';
import {
    User,
    Package,
    DollarSign,
    Truck,
    ArrowLeft,
    CheckCircle2,
    Loader2,
    Search,
    ChevronDown,
    AlertCircle,
    StickyNote,
    Zap
} from 'lucide-react';
import { SIZE_EXPANSION } from '../utils/variantUtils';
const SOURCES = [
    { id: 'whatsapp', label: 'WhatsApp', color: 'text-green-600 bg-green-50' },
    { id: 'tiktok', label: 'TikTok', color: 'text-pink-600 bg-pink-50' },
    { id: 'facebook', label: 'Facebook', color: 'text-blue-600 bg-blue-50' },
    { id: 'instagram', label: 'Instagram', color: 'text-purple-600 bg-purple-50' },
    { id: 'website', label: 'Website', color: 'text-indigo-600 bg-indigo-50' },
    { id: 'other', label: 'Other', color: 'text-gray-600 bg-gray-50' },
];

const CreateOrder = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [providers, setProviders] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(true);
    const [loadingProviders, setLoadingProviders] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const [formData, setFormData] = useState({
        customerName: '',
        phone: '',
        address: '',
        productId: '',
        productName: '',
        variant: { color: '', size: '' },
        quantity: 1,
        price: '',
        note: '',
        source: 'whatsapp',
        sourceDetails: '',
        providerSlug: '',
        cityId: '',
        cityName: '',
        stock: 1,
        unitPrice: '', // Restore for auto-calculation during creation
        assignedTo: '', // For admin to pick an agent
    });

    const [availableVariants, setAvailableVariants] = useState([]);
    const [loadingVariants, setLoadingVariants] = useState(false);

    const [productSearch, setProductSearch] = useState('');
    const [showProductDropdown, setShowProductDropdown] = useState(false);
    const [confirmationAgents, setConfirmationAgents] = useState([]);
    
    // Add explicitly managed cities state
    const [cities, setCities] = useState([]);
    const [loadingCities, setLoadingCities] = useState(false);

    const { user: currentUser } = useAuth();

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const res = await getProducts();
                const productList = Array.isArray(res) ? res : (res.data || []);
                setProducts(productList.filter(p => p.active));
            } catch (err) {
                console.error('Failed to fetch products');
            } finally {
                setLoadingProducts(false);
            }
        };

        const fetchProviders = async () => {
            try {
                const response = await getDeliveryCompanies();
                if (response.success) {
                    const connected = response.data.filter(p => p.isConnected);
                    setProviders(connected);

                    // Intentionally leave providerSlug empty so agents must explicitly select one
                    // to prevent accidental shipments to the wrong provider.
                }
            } catch (err) {
                console.error('Failed to fetch providers');
            } finally {
                setLoadingProviders(false);
            }
        };

        fetchProducts();
        fetchProviders();
    }, []);

    // Fetch confirmation agents (for admin dropdown)
    useEffect(() => {
        const fetchAgents = async () => {
            if (currentUser?.role !== 'admin') return;
            try {
                const response = await getConfirmationAgents();
                if (response.success) {
                    setConfirmationAgents(response.data);
                }
            } catch (err) {
                console.error('Failed to fetch confirmation agents');
            }
        };
        fetchAgents();
    }, [currentUser]);

    // Handle Assigned Agent Change Logic
    useEffect(() => {
        if (!formData.assignedTo) return;

        const selectedAgent = confirmationAgents.find(a => a._id === formData.assignedTo);

        // CASE 1: Agent has NO allowed products
        if (!selectedAgent || !selectedAgent.allowedProducts || selectedAgent.allowedProducts.length === 0) {
            if (formData.productId || formData.productName) {
                setFormData(prev => ({
                    ...prev,
                    productId: '',
                    productName: '',
                    variant: { color: '', size: '' },
                    quantity: 1,
                    price: '',
                    note: prev.note + ' [System: Product cleared - Agent has no allowed products]'
                }));
                setError(`Product cleared: Agent "${selectedAgent?.name}" has no assigned products. Assignment requires product access.`);
                setTimeout(() => setError(null), 5000);
            }
            return;
        }

        // CASE 2: Agent has products, check if CURRENT product is allowed
        if (formData.productName) {
            const isAllowed = selectedAgent.allowedProducts.some(
                pName => pName.toLowerCase() === formData.productName.toLowerCase()
            );

            if (!isAllowed) {
                setFormData(prev => ({
                    ...prev,
                    productId: '',
                    productName: '',
                    variant: { color: '', size: '' },
                    quantity: 1,
                    price: '',
                    note: prev.note + ' [System: Product cleared as it is not allowed for the selected agent]'
                }));
                setError(`Product cleared: Selected agent doesn't have access to "${formData.productName}"`);
                setTimeout(() => setError(null), 5000);
            }
        }
    }, [formData.assignedTo, confirmationAgents, formData.productName, formData.productId]);

    const handleProductSelect = (product) => {
        const qty = Number(formData.quantity) || 1;
        const up = product.defaultPrice || 0;
        setFormData({
            ...formData,
            productId: product._id,
            productName: product.name,
            variant: { color: '', size: '' },
            unitPrice: up,
            price: up * qty
        });
        
        // Fetch variants for selected product
        setLoadingVariants(true);
        getProductVariants(product._id)
            .then(res => {
                setAvailableVariants(res.data || []);
                setLoadingVariants(false);
            })
            .catch(() => setLoadingVariants(false));

        setProductSearch(product.name);
        setShowProductDropdown(false);
    };

    // Auto-fetch cities when provider changes
    useEffect(() => {
        if (formData.providerSlug) {
            setLoadingCities(true);
            ensureCitiesLoaded(formData.providerSlug).then(fetched => {
                setCities(fetched);
                setLoadingCities(false);
            });
        } else {
            setCities([]);
        }
    }, [formData.providerSlug]);

    const handleCityChange = (id, name) => {
        setFormData({ ...formData, cityId: id, cityName: name || '' });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);

        try {
            await createManualOrder(formData);
            navigate('/shipping-queue');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to create order');
        } finally {
            setSubmitting(false);
        }
    };

    const getVisibleProducts = () => {
        if (!formData.assignedTo) return products;

        const selectedAgent = confirmationAgents.find(a => a._id === formData.assignedTo);

        // STRICT: If agent has no allowed products, return EMPTY list to block selection
        if (!selectedAgent || !selectedAgent.allowedProducts || selectedAgent.allowedProducts.length === 0) {
            return [];
        }

        return products.filter(p =>
            selectedAgent.allowedProducts.some(allowedName => allowedName.toLowerCase() === p.name.toLowerCase())
        );
    };

    const filteredProductsList = getVisibleProducts().filter(p =>
        p.name.toLowerCase().includes(productSearch.toLowerCase()) ||
        (p.slug && p.slug.toLowerCase().includes(productSearch.toLowerCase()))
    );

    return (
        <div className="p-6 md:p-8 max-w-5xl mx-auto">
            <button
                onClick={() => navigate('/orders')}
                className="flex items-center gap-2 text-gray-400 hover:text-gray-900 font-semibold text-sm mb-5 transition-colors group"
            >
                <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
                {t('create_order.back_button')}
            </button>

            <div className="flex justify-between items-end mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">{t('create_order.title')}</h1>
                    <p className="text-gray-400 font-medium text-sm mt-1">{t('create_order.subtitle')}</p>
                </div>
                <div className="hidden md:block">
                    <div className="bg-indigo-50 text-indigo-600 px-3.5 py-2 rounded-xl border border-indigo-100 flex items-center gap-2 font-bold text-xs">
                        <Zap size={14} />
                        {t('create_order.manual_mode')}
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-6" autoComplete="off">
                {/* Left Column: Customer & Source */}
                <div className="lg:col-span-2 space-y-5">
                    <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                        <h2 className="text-base font-bold text-gray-900 mb-5 flex items-center gap-2.5">
                            <div className="w-7 h-7 bg-blue-50 text-blue-600 rounded-lg flex items-center justify-center">
                                <User size={15} />
                            </div>
                            {t('create_order.sections.customer')}
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="md:col-span-2">
                                <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1.5 ml-1 block">{t('create_order.form.full_name')}</label>
                                <input
                                    required
                                    type="text"
                                    name="x-cust-fn"
                                    className="w-full px-4 py-3 bg-gray-50/80 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500 font-semibold text-sm text-gray-800 transition-all"
                                    placeholder={t('create_order.form.placeholders.full_name')}
                                    value={formData.customerName}
                                    onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                                    autoComplete="new-password"
                                />
                            </div>
                            <div>
                                <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1.5 ml-1 block">{t('create_order.form.phone')}</label>
                                <input
                                    required
                                    type="text"
                                    name="x-cust-ph"
                                    className="w-full px-4 py-3 bg-gray-50/80 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500 font-semibold text-sm text-gray-800 transition-all"
                                    placeholder={t('create_order.form.placeholders.phone')}
                                    value={formData.phone}
                                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                    autoComplete="new-password"
                                />
                            </div>
                            <div>
                                <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1.5 ml-1 block">{t('create_order.form.source')}</label>
                                <select
                                    required
                                    className="w-full px-4 py-3 bg-gray-50/80 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500 font-semibold text-sm text-gray-800 transition-all appearance-none"
                                    value={formData.source}
                                    onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                                >
                                    {SOURCES.map(s => <option key={s.id} value={s.id}>{t(`create_order.form.sources.${s.id}`)}</option>)}
                                </select>
                            </div>
                            <div className="md:col-span-2">
                                <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1.5 ml-1 block">{t('create_order.form.address')}</label>
                                <textarea
                                    required
                                    rows="2"
                                    name="x-cust-addr"
                                    className="w-full px-4 py-3 bg-gray-50/80 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/10 focus:border-blue-500 font-semibold text-sm text-gray-800 transition-all resize-none"
                                    placeholder={t('create_order.form.placeholders.address')}
                                    value={formData.address}
                                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                    autoComplete="new-password"
                                />
                            </div>
                        </div>
                    </section>

                    <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                        <h2 className="text-base font-bold text-gray-900 mb-5 flex items-center gap-2.5">
                            <div className="w-7 h-7 bg-indigo-50 text-indigo-600 rounded-lg flex items-center justify-center">
                                <Package size={15} />
                            </div>
                            {t('create_order.sections.product')}
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="md:col-span-2 relative">
                                <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1.5 ml-1 block">{t('create_order.form.product')}</label>
                                <div className="relative">
                                    <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                    <input
                                        type="text"
                                        className="w-full pl-10 pr-4 py-3 bg-gray-50/80 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 font-semibold text-sm text-gray-800 transition-all"
                                        placeholder={t('create_order.form.placeholders.product_search')}
                                        value={productSearch}
                                        onChange={(e) => {
                                            setProductSearch(e.target.value);
                                            setShowProductDropdown(true);
                                        }}
                                        onFocus={() => setShowProductDropdown(true)}
                                        autoComplete="off"
                                    />
                                    {showProductDropdown && (
                                        <div className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-gray-100 rounded-xl shadow-xl z-30 max-h-56 overflow-y-auto p-1.5 scrollbar-thin">
                                            {loadingProducts ? (
                                                <div className="p-4 text-center text-gray-400 text-xs font-semibold">{t('create_order.messages.product_loading')}</div>
                                            ) : filteredProductsList.length > 0 ? (
                                                filteredProductsList.map(p => (
                                                    <button
                                                        key={p._id}
                                                        type="button"
                                                        onClick={() => handleProductSelect(p)}
                                                        className="w-full text-left p-3 hover:bg-gray-50 rounded-lg flex items-center justify-between group transition-all"
                                                    >
                                                        <div>
                                                            <div className="font-semibold text-sm text-gray-900">{p.name}</div>
                                                            {p.slug && <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">{p.slug}</div>}
                                                        </div>
                                                        <div className="font-bold text-sm text-indigo-600 group-hover:translate-x-0.5 transition-transform">{p.defaultPrice} {t('common.currency')}</div>
                                                    </button>
                                                ))
                                            ) : (
                                                <div className="p-4 text-center text-red-500 text-xs font-semibold">
                                                    {formData.assignedTo ? t('create_order.messages.agent_no_products') : t('create_order.messages.no_products_found')}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4 bg-indigo-50/20 p-4 rounded-2xl border border-indigo-100/50 relative">
                                <div className="md:col-span-2 flex items-center justify-between mb-1">
                                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-1.5">
                                        <Package size={12} className="text-indigo-500" />
                                        SÉLECTION DE LA VARIANTE
                                    </label>
                                    {loadingVariants && <Loader2 size={12} className="animate-spin text-indigo-400" />}
                                </div>

                                <div>
                                    <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 ml-1">{t('create_order.form.variant_color')}</p>
                                    <select
                                        disabled={availableVariants.length === 0}
                                        className="w-full px-4 py-3 bg-white border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 font-semibold text-sm text-gray-800 transition-all appearance-none"
                                        value={formData.variant.color}
                                        onChange={(e) => setFormData({ 
                                            ...formData, 
                                            variant: { color: e.target.value, size: '' } 
                                        })}
                                    >
                                        <option value="">Sélectionner</option>
                                        {[...new Set(availableVariants.map(v => v.color))].map(c => (
                                            <option key={c} value={c}>{c}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 ml-1">{t('create_order.form.variant_size')}</p>
                                    <select
                                        disabled={!formData.variant.color}
                                        className="w-full px-4 py-3 bg-white border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 font-semibold text-sm text-gray-800 transition-all appearance-none"
                                        value={formData.variant.size}
                                        onChange={(e) => setFormData({ 
                                            ...formData, 
                                            variant: { ...formData.variant, size: e.target.value } 
                                        })}
                                    >
                                        <option value="">Sélectionner</option>
                                        {availableVariants
                                            .filter(v => v.color === formData.variant.color)
                                            .flatMap(v => {
                                                const expanded = SIZE_EXPANSION[v.size];
                                                if (expanded) {
                                                    return expanded.map(label => ({
                                                        label,
                                                        value: label,
                                                        quantity: v.quantity
                                                    }));
                                                }
                                                return [{ label: v.size, value: v.size, quantity: v.quantity }];
                                            })
                                            .map((opt, idx) => (
                                                <option key={`${opt.value}-${idx}`} value={opt.value} disabled={opt.quantity <= 0}>
                                                    {opt.label} {opt.quantity <= 0 ? '(Rupture)' : `(${opt.quantity})`}
                                                </option>
                                            ))
                                        }
                                    </select>
                                </div>

                                {formData.variant.color && formData.variant.size && (
                                    <div className="md:col-span-2 flex items-center gap-2 pt-1">
                                        {(() => {
                                            const v = availableVariants.find(x => x.color === formData.variant.color && x.size === formData.variant.size);
                                            if (!v) return null;
                                            const colorClass = v.quantity <= 0 ? 'bg-red-50 text-red-600 border-red-100' : 
                                                             v.quantity <= 5 ? 'bg-amber-50 text-amber-600 border-amber-100' : 
                                                             'bg-emerald-50 text-emerald-600 border-emerald-100';
                                            return (
                                                <div className={`px-3 py-1 rounded-full border text-[10px] font-black uppercase tracking-widest ${colorClass}`}>
                                                    {v.quantity <= 0 ? '🔴 Rupture de Stock' : v.quantity <= 5 ? `🟡 Stock Faible: ${v.quantity}` : `🟢 En Stock: ${v.quantity}`}
                                                </div>
                                            );
                                        })()}
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1.5 ml-1 block">{t('create_order.form.quantity')}</label>
                                <input
                                    required
                                    type="number"
                                    min="1"
                                    className="w-full px-4 py-3 bg-gray-50/80 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 font-semibold text-sm text-gray-800 transition-all"
                                    value={formData.quantity}
                                    onChange={(e) => {
                                        const qty = e.target.value;
                                        const q = Number(qty) || 0;
                                        const up = Number(formData.unitPrice) || 0;
                                        setFormData({
                                            ...formData,
                                            quantity: qty,
                                            price: up > 0 ? (up * q) : formData.price
                                        });
                                    }}
                                />
                            </div>
                            <div>
                                <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1.5 ml-1 block">{t('create_order.form.price', { currency: t('common.currency') })}</label>
                                <div className="relative">
                                    <DollarSign className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-300" size={16} />
                                    <input
                                        required
                                        type="number"
                                        className="w-full pl-10 pr-4 py-3 bg-gray-50/80 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 font-semibold text-sm text-gray-800 transition-all"
                                        placeholder={t('create_order.form.placeholders.price')}
                                        value={formData.price}
                                        onChange={(e) => {
                                            const total = e.target.value;
                                            const t = Number(total) || 0;
                                            const q = Number(formData.quantity) || 1;
                                            setFormData({
                                                ...formData,
                                                price: total,
                                                unitPrice: q > 0 ? (t / q).toFixed(2) : 0
                                            });
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </section>
                </div>

                {/* Right Column: Shipping & Summary */}
                <div className="space-y-5">
                    <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                        <h2 className="text-base font-bold text-gray-900 mb-5 flex items-center gap-2.5">
                            <div className="w-7 h-7 bg-green-50 text-green-600 rounded-lg flex items-center justify-center">
                                <Truck size={15} />
                            </div>
                            {t('create_order.sections.shipping')}
                        </h2>
                        <div className="space-y-4">
                            <div>
                                <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1.5 ml-1 block">{t('create_order.form.provider')}</label>
                                <select
                                    required
                                    className={`w-full px-4 py-3 bg-gray-50/80 border ${providers.length === 0 ? 'border-red-100' : 'border-gray-100'} rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/10 focus:border-green-500 font-semibold text-sm text-gray-800 transition-all appearance-none`}
                                    value={formData.providerSlug}
                                    onChange={(e) => setFormData({ ...formData, providerSlug: e.target.value, cityId: '', cityName: '' })}
                                >
                                    {loadingProviders ? (
                                        <option value="">{t('create_order.form.status.loading_providers')}</option>
                                    ) : providers.length > 0 ? (
                                        <>
                                            <option value="">{t('create_order.form.placeholders.select_provider')}</option>
                                            {providers.map(p => <option key={p.slug} value={p.slug}>{p.name}</option>)}
                                        </>
                                    ) : (
                                        <option value="">{t('create_order.form.status.no_providers')}</option>
                                    )}
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1.5 ml-1 block">{t('create_order.form.validated_city')}</label>
                                <CityCombobox
                                    cities={cities}
                                    value={formData.cityId}
                                    onChange={handleCityChange}
                                    placeholder={t('create_order.form.placeholders.select_city')}
                                    disabled={!formData.providerSlug}
                                    isLoading={loadingCities}
                                />
                            </div>
                            <div>
                                <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1.5 ml-1 block">{t('create_order.form.parcel_type')}</label>
                                <div className="flex bg-gray-50/80 p-1 rounded-xl border border-gray-100">
                                    <button
                                        type="button"
                                        onClick={() => setFormData({ ...formData, stock: 1 })}
                                        className={`flex-1 py-2 rounded-lg text-[10px] font-bold uppercase tracking-widest transition-all ${formData.stock === 1 ? 'bg-indigo-600 text-white shadow-md shadow-indigo-200' : 'text-gray-400 hover:text-gray-600'}`}
                                    >
                                        {t('create_order.form.stock')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setFormData({ ...formData, stock: 0 })}
                                        className={`flex-1 py-2 rounded-lg text-[10px] font-bold uppercase tracking-widest transition-all ${formData.stock === 0 ? 'bg-indigo-600 text-white shadow-md shadow-indigo-200' : 'text-gray-400 hover:text-gray-600'}`}
                                    >
                                        {t('create_order.form.pickup')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Assignment Section */}
                    <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                        <h2 className="text-base font-bold text-gray-900 mb-5 flex items-center gap-2.5">
                            <div className="w-7 h-7 bg-amber-50 text-amber-600 rounded-lg flex items-center justify-center">
                                <User size={15} />
                            </div>
                            {t('create_order.sections.assignment')}
                        </h2>
                        {currentUser?.role === 'admin' ? (
                            <div>
                                <label className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1.5 ml-1 block">
                                    {t('create_order.form.agent')}
                                </label>
                                <select
                                    className="w-full px-4 py-3 bg-gray-50/80 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/10 focus:border-amber-500 font-semibold text-sm text-gray-800 transition-all appearance-none"
                                    value={formData.assignedTo}
                                    onChange={(e) => setFormData({ ...formData, assignedTo: e.target.value })}
                                >
                                    <option value="">{t('create_order.form.status.unassigned')}</option>
                                    {confirmationAgents.map(agent => (
                                        <option key={agent._id} value={agent._id}>{agent.name} ({agent.email})</option>
                                    ))}
                                </select>
                            </div>
                        ) : (
                            <div className="p-3.5 bg-green-50 border border-green-100 rounded-xl flex items-center gap-3 text-green-700">
                                <CheckCircle2 size={18} />
                                <span className="font-semibold text-sm">{t('create_order.form.assigned_to_me', { name: currentUser?.name })}</span>
                            </div>
                        )}
                    </section>

                    <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                        <h2 className="text-base font-bold text-gray-900 mb-5 flex items-center gap-2.5">
                            <div className="w-7 h-7 bg-gray-100 text-gray-500 rounded-lg flex items-center justify-center">
                                <StickyNote size={15} />
                            </div>
                            {t('create_order.sections.note')}
                        </h2>
                        <textarea
                            rows="3"
                            className="w-full px-4 py-3 bg-gray-50/80 border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-500/10 focus:border-gray-400 font-medium text-sm text-gray-600 transition-all resize-none"
                            placeholder={t('create_order.form.placeholders.notes')}
                            value={formData.note}
                            onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                        />
                    </section>

                    <div className="pt-1">
                        {error && (
                            <div className="mb-3 p-3.5 bg-red-50 border border-red-100 rounded-xl flex items-center gap-3 text-red-600">
                                <AlertCircle size={18} />
                                <span className="text-xs font-semibold">{error}</span>
                            </div>
                        )}
                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full py-4 bg-gray-900 hover:bg-indigo-600 text-white rounded-2xl font-bold transition-all duration-300 shadow-lg shadow-gray-200 flex items-center justify-center gap-2.5 text-sm disabled:opacity-50"
                        >
                            {submitting ? <Loader2 className="animate-spin" size={20} /> : <CheckCircle2 size={20} />}
                            {t('orders.add_order')}
                        </button>
                    </div>
                </div>
            </form>

            <div className="mt-10 text-center text-gray-300 font-semibold text-[10px] uppercase tracking-[0.2em] flex items-center justify-center gap-5">
                <div className="flex items-center gap-1.5"><div className="w-1 h-1 rounded-full bg-blue-400" /> {t('create_order.footer.secure')}</div>
                <div className="flex items-center gap-1.5"><div className="w-1 h-1 rounded-full bg-indigo-400" /> {t('create_order.footer.autosync')}</div>
                <div className="flex items-center gap-1.5"><div className="w-1 h-1 rounded-full bg-green-400" /> {t('create_order.footer.api')}</div>
            </div>
        </div>
    );
};

export default CreateOrder;
